import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { Timestamp } from "firebase/firestore";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useAuth, logEvent, logException } from "@um/ui";
import {
  CANCELLATION_REASON_LABELS,
  CANCELLATION_REASONS,
  type CancellationReason,
} from "@um/contracts";
import {
  usePaymentsStore,
  selectIsPaymentUiOpen,
  type SubscriptionSnapshot,
} from "@/store/usePaymentsStore";
import { submitCancellationFeedback } from "@/services/feedback";

/** Account types that should never see the exit survey. */
const EXCLUDED_ACCOUNT_TYPES = ["bot", "anonymous"];

/** Small pause after the trigger so the drawer doesn't pop mid-transition. */
const OPEN_DELAY_MS = 1200;

/** One flag per cancelled subscription period, so a re-subscribe + re-cancel asks again. */
function surveyKey(subscription: SubscriptionSnapshot): string {
  return `${subscription.entitlementId}_${subscription.purchaseDate.getTime()}`;
}

/**
 * Exit survey for subscription cancellations. Mounted once at the app root
 * (like `ReportPageDrawer`): when the RevenueCat snapshot shows a
 * subscription newly set to not renew (`active_non_renewing` — the user
 * cancelled in the store or via the manage sheet), it opens once per
 * cancellation and asks why, writing the answer to the same
 * `Account/{id}/Feedback` collection the admin grid reviews.
 *
 * Owner-only: subscriptions live in the grown-up console, so kids and
 * members never see this (BLUEPRINT §2).
 */
export function CancellationSurveyDrawer() {
  const { accountId, account, role, settings, updateAccountSettings } =
    useAuth();
  const subscriptions = usePaymentsStore((s) => s.subscriptions);
  const paymentsReady = usePaymentsStore((s) => s.isReady);
  const isPaymentUiOpen = usePaymentsStore(selectIsPaymentUiOpen);

  const [isOpen, setIsOpen] = useState(false);
  const [target, setTarget] = useState<SubscriptionSnapshot | null>(null);
  const [reason, setReason] = useState<CancellationReason | null>(null);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const candidate = useMemo(() => {
    if (!paymentsReady) return null;
    return (
      subscriptions.find(
        // Promotional grants (e.g. the account-creation free-access promo)
        // never auto-renew, so they always report active_non_renewing —
        // that's not a cancellation, so they never trigger the exit survey.
        (s) =>
          s.lifecycleStatus === "active_non_renewing" &&
          s.purchaseStore !== "PROMOTIONAL"
      ) ?? null
    );
  }, [subscriptions, paymentsReady]);

  useEffect(() => {
    if (!candidate || isOpen || sending) return;
    if (!accountId || role !== "owner") return;
    if (account?.type && EXCLUDED_ACCOUNT_TYPES.includes(account.type)) return;
    if (isPaymentUiOpen) return;

    const key = surveyKey(candidate);
    if (settings?.retention?.cancelSurveyShown?.[key]) return;

    const timer = setTimeout(() => {
      // Mark shown immediately so the survey never nags, even if dismissed.
      updateAccountSettings({
        retention: {
          cancelSurveyShown: {
            ...settings?.retention?.cancelSurveyShown,
            [key]: Timestamp.now(),
          },
        },
      }).catch((e) => {
        console.warn("[cancelSurvey] Failed to record survey-shown flag", e);
      });
      setTarget(candidate);
      setIsOpen(true);
      logEvent("um_cancel_survey_shown", {
        accountId,
        entitlementId: candidate.entitlementId,
        productIdentifier: candidate.productIdentifier,
        periodType: candidate.periodType,
      });
    }, OPEN_DELAY_MS);
    return () => clearTimeout(timer);
  }, [
    candidate,
    isOpen,
    sending,
    accountId,
    role,
    account?.type,
    isPaymentUiOpen,
    settings?.retention?.cancelSurveyShown,
    updateAccountSettings,
  ]);

  const resetForm = () => {
    setReason(null);
    setMessage("");
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setIsOpen(false);
      resetForm();
      logEvent("um_cancel_survey_dismissed", { accountId });
    }
  };

  const canSubmit = !!accountId && (!!reason || !!message.trim());

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || sending) return;

    setSending(true);
    try {
      await submitCancellationFeedback(accountId!, {
        reason: reason ?? undefined,
        message,
        entitlementId: target?.entitlementId,
        productIdentifier: target?.productIdentifier,
      });
      toast.success("Thank you — this genuinely shapes what we build next.");
      setIsOpen(false);
      resetForm();
    } catch (error) {
      logException(error);
      console.error("[cancelSurvey] Failed to submit", error);
      toast.error("Couldn't send your answer. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const accessUntil = target?.expiresDate
    ? target.expiresDate.toLocaleDateString(undefined, {
        month: "long",
        day: "numeric",
      })
    : null;

  return (
    <Drawer open={isOpen} onOpenChange={handleOpenChange}>
      <DrawerContent className="max-h-[85vh]">
        <form
          onSubmit={handleSubmit}
          className="flex min-h-0 flex-col overflow-y-auto"
        >
          <DrawerHeader>
            <DrawerTitle>Before you go — one quick question 💜</DrawerTitle>
            <DrawerDescription>
              We saw you turned off renewal — totally fine
              {accessUntil ? `, and you keep everything until ${accessUntil}` : ""}
              . Would you tell us why? We're a small team and every answer
              shapes what we build next.
            </DrawerDescription>
          </DrawerHeader>

          <div className="flex flex-col gap-3 px-4">
            <div className="flex flex-wrap gap-2">
              {CANCELLATION_REASONS.map((option) => {
                const selected = reason === option;
                return (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setReason(selected ? null : option)}
                    aria-pressed={selected}
                    className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                      selected
                        ? "border-purple-600 bg-purple-600 text-white"
                        : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {CANCELLATION_REASON_LABELS[option]}
                  </button>
                );
              })}
            </div>

            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="What would have made you stay? (optional)"
              rows={4}
              className="w-full resize-none"
            />
          </div>

          <DrawerFooter>
            <Button
              type="submit"
              className="w-full"
              disabled={!canSubmit || sending}
            >
              {sending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Send className="mr-2 h-4 w-4" />
              )}
              {sending ? "Sending…" : "Send answer"}
            </Button>
          </DrawerFooter>
        </form>
      </DrawerContent>
    </Drawer>
  );
}
